import { useMemo } from 'react';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';
import {
  buildRecentTradeRows,
  buildStrategyRecentStats,
} from '../../services/strategyStats/strategyStatsService';
import '../Styles/GEMICPage.css';

const snapshotCards = [
  {
    label: 'Strategy Type',
    value: '1-DTE IC',
    detail: 'Daily SPX iron condor',
  },
  {
    label: 'Strike Logic',
    value: 'Expected Move',
    detail: 'Short strikes built from projected range',
  },
  {
    label: 'Risk Profile',
    value: 'Defined Risk',
    detail: 'Fixed 20-point wings',
  },
  {
    label: 'Role',
    value: 'Daily Anchor',
    detail: 'Core systematic premium engine',
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Market Data Snapshot',
    text: 'Near the end of the session, SPX and volatility data are captured to estimate the next-session expected move.',
  },
  {
    step: '02',
    title: 'Expected Move Calculation',
    text: 'SPX, VIX1D, VIX, and VVIX are used to define the projected 1-DTE range for the next trading session.',
  },
  {
    step: '03',
    title: 'Strike Construction',
    text: 'Short strikes are selected around SPX Gamma proprietary expected-move levels, with long wings placed 20 points beyond.',
  },
  {
    step: '04',
    title: 'Next-Day Settlement',
    text: 'The position is structured before entry and settles against the next trading day’s expiration level.',
  },
];

const monteCarloStats = [
  {
    label: 'Simulations',
    value: '10,000',
  },
  {
    label: 'Median Ending Equity',
    value: '$61,895',
  },
  {
    label: '5th Percentile Equity',
    value: '$35,255',
  },
  {
    label: '95th Percentile Equity',
    value: '$87,361',
  },
  {
    label: 'Failure Paths',
    value: '2.24%',
    tone: 'negative',
  },
  {
    label: 'Median Return',
    value: '+518.95%',
    tone: 'positive',
  },
];

function StatCard({ label, value, detail, helper, tone }) {
  return (
    <div className={`gemicStatCard ${tone ? `gemicStatCard--${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
      {helper ? <small>{helper}</small> : null}
    </div>
  );
}

export default function GEMICPage() {
  const gemicPayload = useStrategyDashboardStore(
    state => state.strategies?.gemic
  );

  const gemicRecentTrades = gemicPayload?.recent_trades || [];

  const gemicClosedTrades = useMemo(
    () =>
      gemicRecentTrades.filter(
        trade => String(trade.status || '').toUpperCase() === 'CLOSED'
      ),
    [gemicRecentTrades]
  );

  const gemicStats = useMemo(
    () => buildStrategyRecentStats(gemicClosedTrades, { limit: 25 }),
    [gemicClosedTrades]
  );

  const gemicTradeRows = useMemo(
    () => buildRecentTradeRows(gemicClosedTrades, { limit: 10 }),
    [gemicClosedTrades]
  );

  return (
    <main className="gemicPage">
      <section className="gemicPageGrid">
        <div className="gemicLeftColumn">
          <section className="gemicHeroContent">
            <div className="gemicEyebrow">SPX Gamma Premium Sellers Suite</div>

            <h1>GEMIC</h1>

            <h2>Gamma Expected Move Iron Condor</h2>

            <p>
              GEMIC is the systematic daily 1-DTE premium-selling engine of the
              SPX Gamma suite. It converts the next-session expected move into a
              defined-risk SPX iron condor, giving traders a repeatable structure
              without requiring a daily directional prediction.
            </p>

            <p>
              The strategy uses SPX, VIX1D, VIX, and VVIX to estimate the
              market’s projected next-session range, then builds short strikes
              around that expected-move framework with fixed 20-point wings.
            </p>

            <div className="gemicHeroActions">
              <a
                className="gemicPrimaryButton"
                href="https://spxgamma-gemic-strategy-framework.spxgamma.com/"
                target="_blank"
                rel="noreferrer"
              >
                View Full GEMIC Framework
              </a>
            </div>
          </section>

          <section className="gemicStatsGrid">
            {snapshotCards.map(card => (
              <StatCard
                key={card.label}
                label={card.label}
                value={card.value}
                detail={card.detail}
              />
            ))}
          </section>

          <section className="gemicPanel">
            <div className="gemicPanelHeader">
              <div>
                <span className="gemicSectionLabel">Strategy Role</span>
                <h3>The Daily Anchor Strategy</h3>
              </div>

              <div className="gemicPanelPill">1 Trade Per Day</div>
            </div>

            <p className="gemicPanelText">
              GEMIC is the steady daily layer of the SPX Gamma Premium Sellers
              suite. MEIC handles intraday 0-DTE opportunities, ACCE targets
              more selective aggressive 1-DTE dislocations, and ARGOS adapts
              across multi-day regime windows. GEMIC anchors the system with a
              repeatable expected-move-based trade structure.
            </p>

            <p className="gemicPanelText">
              The goal is not to predict whether SPX will finish higher or lower
              the next day. The goal is to define a reasonable range, collect
              premium around that range, and keep the risk fixed before the
              position is opened.
            </p>
          </section>

          <section className="gemicPanel">
            <div className="gemicPanelHeader">
              <div>
                <span className="gemicSectionLabel">Execution Flow</span>
                <h3>Expected Move Process</h3>
              </div>

              <div className="gemicPanelPill">5 Min Before Close</div>
            </div>

            <div className="gemicProcessGrid">
              {processSteps.map(item => (
                <div className="gemicProcessCard" key={item.step}>
                  <div className="gemicProcessStep">{item.step}</div>
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="gemicPanel">
            <div className="gemicPanelHeader">
              <div>
                <span className="gemicSectionLabel">Trade History</span>
                <h3>Latest 10 Closed GEMIC Trades</h3>
                <p className="gemicPanelSubtext">
                  Displaying the latest 10 closed trades. Statistics are calculated from
                  the latest 25 closed GEMIC trades.
                </p>
              </div>

              <div className="gemicPanelPill">Daily 1-DTE</div>
            </div>

            <div className="gemicRecentTradeTableWrap">
              <table className="gemicRecentTradeTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Expiration</th>
                    <th>Structure</th>
                    <th>Credit</th>
                    <th>Result</th>
                    <th>P&L</th>
                  </tr>
                </thead>

                <tbody>
                  {gemicTradeRows.length > 0 ? (
                    gemicTradeRows.map((trade, index) => (
                      <tr key={trade.id || `${trade.date}-${index}`}>
                        <td>{trade.date}</td>
                        <td>{trade.expiration}</td>
                        <td>{trade.structure}</td>
                        <td>{trade.credit}</td>
                        <td>
                          <span
                            className={`gemicResultPill gemicResultPill--${trade.tone}`}
                          >
                            {trade.result}
                          </span>
                        </td>
                        <td className={`gemicPnlCell gemicPnlCell--${trade.tone}`}>
                          {trade.pnl}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="gemicEmptyTableCell" colSpan="6">
                        No closed GEMIC trades available yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="gemicRightColumn">
          <section className="gemicPanel gemicPerformancePanel">
            <div className="gemicPanelHeader">
              <div>
                <span className="gemicSectionLabel">Performance</span>
                <h3>Recent GEMIC Statistics</h3>
              </div>

              <span className="gemicPanelPill">Last 25 Trades</span>
            </div>

            <div className="gemicMiniStatsGrid">
              {gemicStats.map(stat => (
                <StatCard
                  key={stat.label}
                  label={stat.label}
                  value={stat.value}
                  helper={stat.helper}
                  tone={stat.tone}
                />
              ))}
            </div>

            <p className="gemicPanelSubtext">
              GEMIC’s edge comes from high win rate and repeatable premium
              collection, but average losses are materially larger than average
              wins. Strike placement, defined risk, and sizing discipline remain
              critical.
            </p>
          </section>

          <section className="gemicPanel">
            <div className="gemicPanelHeader">
              <div>
                <span className="gemicSectionLabel">Monte Carlo</span>
                <h3>Path Validation</h3>
              </div>
            </div>

            <div className="gemicMiniStatsGrid gemicMiniStatsGrid--single">
              {monteCarloStats.map(stat => (
                <StatCard
                  key={stat.label}
                  label={stat.label}
                  value={stat.value}
                  tone={stat.tone}
                />
              ))}
            </div>

            <p className="gemicPanelSubtext">
              Monte Carlo testing helps determine whether the strategy’s result
              depends on one favorable historical sequence or remains resilient
              across many randomized trade paths.
            </p>
          </section>

          <section className="gemicPanel">
            <div className="gemicPanelHeader">
              <div>
                <span className="gemicSectionLabel">Management Framework</span>
                <h3>Hold-to-OPEX Baseline</h3>
              </div>
            </div>

            <div className="gemicInfoStack">
              <div className="gemicInfoBlock">
                <span>Baseline</span>
                <p>
                  GEMIC was backtested using a hold-to-expiration approach. This
                  creates a clean benchmark because each trade is allowed to
                  settle against its final 1-DTE payoff structure.
                </p>
              </div>

              <div className="gemicInfoBlock">
                <span>Active Management</span>
                <p>
                  Traders may choose to manage positions early, but managed
                  results will naturally differ from the historical
                  hold-to-expiration results.
                </p>
              </div>

              <div className="gemicInfoBlock">
                <span>Risk Control</span>
                <p>
                  GEMIC does not depend on averaging down, rolling indefinitely,
                  or adding unplanned exposure. The full structure is known
                  before entry.
                </p>
              </div>
            </div>
          </section>
        </aside>
      </section>

      <section className="gemicDisclaimer">
        GEMIC trade posts are for educational and informational purposes only.
        They are not financial advice or a recommendation to buy or sell any
        security or options contract. Options trading involves risk and may not
        be suitable for all traders.
      </section>
    </main>
  );
}